/**
 * Snackbar composable for global notifications
 *
 * Usage:
 *   const { show, success, error, warning, info } = useSnackbar()
 *
 *   show('Custom message', { color: 'primary', timeout: 5000 })
 *   success('Operation completed!')
 *   error('Something went wrong')
 *   warning('Please review your input')
 *   info('New updates available')
 */

// Snackbar icons by type
const SNACKBAR_ICONS = {
  success: 'tabler-circle-check',
  error: 'tabler-alert-circle',
  warning: 'tabler-alert-triangle',
  info: 'tabler-info-circle',
}

// Default timeout values by type (ms)
const SNACKBAR_TIMEOUTS = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 4000,
  default: 3000,
}

// Global snackbar state
const snackbarState = ref({
  visible: false,
  message: '',
  color: 'success',
  icon: null,
  timeout: SNACKBAR_TIMEOUTS.default,
})

export function useSnackbar() {
  /**
   * Show a snackbar with custom options
   * @param {string} message - Message to display
   * @param {Object} options - Snackbar options
   * @param {string} options.color - Snackbar color (success, error, warning, info, primary, etc.)
   * @param {number} options.timeout - Auto-hide timeout in ms (0 = no auto-hide)
   * @param {string} options.icon - Custom icon (optional)
   */
  function show(message, options = {}) {
    const color = options.color || 'success'

    snackbarState.value = {
      visible: true,
      message,
      color,
      icon: options.icon ?? SNACKBAR_ICONS[color] ?? null,
      timeout: options.timeout ?? SNACKBAR_TIMEOUTS[color] ?? SNACKBAR_TIMEOUTS.default,
    }
  }

  /**
   * Hide the snackbar
   */
  function hideSnackbar() {
    snackbarState.value.visible = false
  }

  /**
   * Show a success snackbar
   * @param {string} message
   * @param {Object} options
   */
  function success(message, options = {}) {
    show(message, { color: 'success', ...options })
  }

  /**
   * Show an error snackbar
   * @param {string} message
   * @param {Object} options
   */
  function error(message, options = {}) {
    show(message, { color: 'error', ...options })
  }

  /**
   * Show a warning snackbar
   * @param {string} message
   * @param {Object} options
   */
  function warning(message, options = {}) {
    show(message, { color: 'warning', ...options })
  }

  /**
   * Show an info snackbar
   * @param {string} message
   * @param {Object} options
   */
  function info(message, options = {}) {
    show(message, { color: 'info', ...options })
  }

  return {
    // State
    snackbarState,

    // Methods
    show,
    hideSnackbar,
    success,
    error,
    warning,
    info,
  }
}
